import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Tab, Tabs } from '@mui/material';
import { useQuery } from 'react-query';

import useTopPanel from 'hooks/useTopPanel';
import useSetQuery from 'hooks/useSetQuery';
import useSearch from 'hooks/useSearch';
import PaymentBankTable from 'components/ui/tables/PaymentBankTable';
import BankFiltersComponent from 'components/ui/filters/BankFiltersComponent';
import SearchInputNavbar from 'components/SearchInputNavbar';
import PaymentBankViewModal from './PaymentBankViewModal';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import SetContractModal from './SetContractModal';

export default memo(function PaymentBank() {
  const { setComponent } = useTopPanel();
  const axiosPrivate = useAxiosPrivate();
  const query = useSearch();
  const setQuery = useSetQuery();
  const { t, i18n } = useTranslation();
  const [refetch, setRefetch] = useState(false);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [hasError, setHasError] = useState(false);
  const [openContractModal, setOpenContractModal] = useState(false);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['paymentBanks', query, refetch],
    queryFn: async function () {
      const response = await axiosPrivate.get('/admin/bank/counts');
      return response.data.data;
    },
    enabled: !hasError,
    onError: (error) => {
      setHasError(true);
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  useEffect(() => {
    setComponent(<div className='component-title'>{t('bank.title')}</div>);
  }, [i18n.language]);

  const onChange = (i, val) => {
    setQuery('type', val);
    setQuery('page');
  };

  return (
    <div className='component-list-wrapper'>
      <div className='component-list-header mb-2'>
        <div className='header-actions-container pt-2 pb-2 flex flex-row items-center'>
          <div className='header-actions filter-box flex items-center grow'>
            <div className='flex gap-2 min-w-fit items-center mr-2'>
              <Button
                variant='base'
                color='primary'
                startIcon={<i className='bi bi-filter' />}
                onClick={() => setExpanded((prev) => !prev)}
              >
                {t('common.button.filter')}
              </Button>
              <Tabs
                value={query.get('type') || ''}
                sx={{ minHeight: 32, height: 32 }}
                onChange={onChange}
                className='base-border !rounded-[10px] !h-[45px] bg-white [&_.MuiTabs-flexContainer]:pt-2'
              >
                <Tab
                  label={
                    <span className='flex items-center justify-between'>
                      {t('bank.tabs.all')}&nbsp;
                      <span className='rounded-xl bg-blue-200 py-0.5 px-1 text-sm'>
                        {refetch || isLoading || isFetching ? (
                          <i className={`bi bi-arrow-repeat animate-spin`} />
                        ) : (
                          data?.all
                        )}
                      </span>
                    </span>
                  }
                  value=''
                  sx={{ minHeight: 30, height: 30 }}
                />
                <Tab
                  label={
                    <span className='flex items-center justify-between'>
                      {t('bank.tabs.rejected')}&nbsp;
                      <span className='rounded-xl bg-blue-200 py-0.5 px-1 text-sm'>
                        {refetch || isLoading || isFetching ? (
                          <i className={`bi bi-arrow-repeat animate-spin`} />
                        ) : (
                          data?.unaccepted
                        )}
                      </span>
                    </span>
                  }
                  value='unaccept'
                  sx={{ minHeight: 30, height: 30 }}
                />
                <Tab
                  label={
                    <span className='flex items-center justify-between'>
                      {t('bank.tabs.accepted')}&nbsp;
                      <span className='rounded-xl bg-blue-200 py-0.5 px-1 text-sm'>
                        {refetch || isLoading || isFetching ? (
                          <i className={`bi bi-arrow-repeat animate-spin`} />
                        ) : (
                          data?.accepted
                        )}
                      </span>
                    </span>
                  }
                  value='accepted'
                  sx={{ minHeight: 30, height: 30 }}
                />
              </Tabs>
            </div>
            <div className='w-full'>
              <SearchInputNavbar inputKey='name' />
            </div>
          </div>
          <div className='header-actions action-buttons-box flex items-center justify-center ml-4'>
            <Button
              variant='base'
              className='!mr-1'
              color='info'
              onClick={() => {
                setRefetch(true);
              }}
              disable={`${refetch}`}
            >
              <i
                className={`bi bi-arrow-repeat${
                  refetch ? ' animate-spin' : ''
                }`}
              />
            </Button>
            <Button
              variant='base'
              color='default'
              onClick={() => setOpen(true)}
            >
              <i className='bi bi-gear' />
            </Button>
          </div>
        </div>
        {expanded && (
          <div className='base-border bg-white px-4 pt-2 w-full mb-2'>
            <BankFiltersComponent />
          </div>
        )}
        <div
          className={`component-table-wrapper ${
            expanded ? 'h-[calc(100vh-240px)]' : 'h-[calc(100vh-154px)]'
          }`}
        >
          <PaymentBankTable
            emitRefetch={{ refetch, setRefetch }}
            dataPath={'admin/bank/index'}
            emitTableColumns={{ open, setOpen }}
            tableName='payments/bank'
            actionModal={{
              setOpen: setViewModalOpen,
              setData: setSelected,
              icon: 'bi bi-arrows-angle-expand',
            }}
            setOpenContractModal={setOpenContractModal}
            actionAccept={{
              link: 'admin/bank/pay',
              successMsg: 'Success',
            }}
            actionReject={{
              link: 'admin/bank/132132',
              successMsg: 'Success',
            }}
            actionDelete={{
              link: 'admin/bank/remove',
              key: 'id',
            }}
          />
        </div>
        <PaymentBankViewModal
          open={viewModalOpen}
          setOpen={setViewModalOpen}
          setOpenContractModal={setOpenContractModal}
          refetch={() => setRefetch(true)}
          data={selected}
          refetching={refetch}
        />
        <SetContractModal
          open={openContractModal}
          setOpen={setOpenContractModal}
          refetch={() => setRefetch(true)}
          data={selected}
        />
      </div>
    </div>
  );
});
